import { Injectable, inject } from '@angular/core';
import {
  and,
  AndFilter,
  filter,
  not,
  or,
  ProductTreePath,
  RoleService,
  SearchFilterOperator,
  SearchSort,
  SearchSortDirection,
  SearchSortFieldName,
  toBoolean,
  toEnumValue,
  toStringArray,
  value,
  ValueFilter
} from '@forgerock/backstage-ui-core';
import { firstValueFrom } from 'rxjs';
import { AppSearchParamsExt, AppSearchParamsInt, legacyParamMappings, legacyParams } from '../model/app-search-params';
import { EverythingSearchParamsOptions } from '../model/everything-search-params';
import { FAMILY_ID, MINOR_VERSION, PRODUCT_ID, VERSION } from '../model/product-path-keys';
import { Source } from '../model/search-constants';
import { SearchFilterName } from '../model/search-filter-name';
import { Tab, tabLookup } from '../model/tabs';
import { SearchProductTreeService } from '../search-product-tree.service';

@Injectable({
  providedIn: 'root'
})
export class SearchParamsMapperService {
  private roleService = inject(RoleService);
  private productTreeService = inject(SearchProductTreeService);


  async toSearchParamsInt(params: Partial<AppSearchParamsExt>): Promise<AppSearchParamsInt> {
    params = this.handleLegacyParamsExt(params);

    let tab = tabLookup[ params.t ] ?? tabLookup.all as Tab;
    if (!(await firstValueFrom(this.roleService.hasAny(tab.readPermissions)))) {
      tab = tabLookup.all;
    }

    const productVersionPath = await this.toProductVersionPath(params.productVersionPath);

    return {
      tab,
      page: parseInt(params.page ?? '1', 10),
      sort: params.sort ? SearchSort.parse(params.sort) : new SearchSort(),
      query: params.q ?? '',
      source: toStringArray(params.source).map(it => toEnumValue(Source, it)),
      library: toStringArray(params.library),
      tags: toStringArray(params.tag),
      publicationType: toStringArray(params.type),
      productVersionPath,
      eosl: await this.toEosl(params.eosl, productVersionPath),
      priority: toStringArray(params.priority),
      status: toStringArray(params.status),
      project: toStringArray(params.project),
      entityType: toStringArray(params.entityType)
    };
  }

  private async toProductVersionPath(path: string | string[]): Promise<ProductTreePath[]> {
    const paths = toStringArray(path)
      .map(it => ProductTreePath.parse(it))
      .filter(it => it.toMap()[ PRODUCT_ID ])
      .map(it => this.fixBrokenPaths(it));

    return (await Promise.all(paths)).filter(it => !!it);
  }

  private async toEosl(eosl: string, productVersionPaths: ProductTreePath[]): Promise<boolean> {
    if (toBoolean(eosl)) {
      return true;
    }

    for (const path of productVersionPaths) {
      const segments = path.toMap();
      const version = await this.productTreeService.getVersion(segments[ PRODUCT_ID ], segments[ MINOR_VERSION ]);
      const isEosl = this.productTreeService.isEosl(version);

      if (isEosl) {
        return true;
      }
    }

    return false;
  }

  /**
   * Attempts to add the familyId and minorVersion to a path if they are missing.
   *
   * @private
   */
  private async fixBrokenPaths(path: ProductTreePath): Promise<ProductTreePath> | null {
    if (!path.toMap()[ FAMILY_ID ]) {
      const familyId = await this.productTreeService.getFamilyId(path.toMap()[ PRODUCT_ID ]);

      if (!familyId) {
        return null; // If we don't know the familyId then we can't create a useful path
      }

      path = path.insertBefore(PRODUCT_ID, [ FAMILY_ID, familyId ]);
    }

    if (!path.toMap()[ MINOR_VERSION ]) {
      const minorVersion = await this.productTreeService.getLatestVersionTitle(path.toMap()[ PRODUCT_ID ]);

      if (minorVersion) {
        path = path.insertBefore(VERSION, [ MINOR_VERSION, minorVersion ]);
      }
    }

    return path;
  }

  emptySearchParamsInt(): Promise<AppSearchParamsInt> {
    return this.toSearchParamsInt({});
  }

  /**
   * Removes any params which are not applicable on the current tab
   */
  filterParamsForTab(params: AppSearchParamsInt): Partial<AppSearchParamsInt> {
    return params.tab?.params.reduce((paramsSubset, key) => (
      { ...paramsSubset, [ key ]: params[ key ] }
    ), {} as Partial<AppSearchParamsInt>);
  }

  /**
   * Looks for any known old query param names and replaces them with the correct new param name
   */
  handleLegacyParamsExt(params: { [ key: string ]: any }): Partial<AppSearchParamsExt> {
    const mappedParams: Partial<AppSearchParamsExt> = {};

    // handle specific key=value => {params} mappings
    for (const { from, to } of legacyParamMappings) {
      if (params[ from.key ] === from.value) {
        Object.assign(mappedParams, to);
      }
    }

    // handle renamed params
    for (const param of legacyParams) {
      const toValue = param.legacyKeys.map(it => params[ it ]).find(it => !!it);
      if (toValue && !params[ param.key ]) {
        Object.assign(mappedParams, { [ param.key ]: toValue });
      }
    }

    // provides backwards compatibility for apps only setting version
    const minorVersion = params[ 'minorVersion' ] ?? params[ 'version' ];

    // handle familyId, productId and version params
    const segments = [
      [ FAMILY_ID, params[ 'familyId' ] ],
      [ PRODUCT_ID, params[ 'productId' ] ],
      [ MINOR_VERSION, minorVersion ],
      [ VERSION, params[ 'minorVersion' ] ? params[ 'version' ] : null ]
    ].filter(segment => !!segment[ 1 ]) as [ string, string ][];

    if (segments.length) {
      Object.assign(mappedParams, { productVersionPath: new ProductTreePath(segments).toString() });
    }

    return { ...params, ...mappedParams };
  }

  toSearchParamsExt(params: Partial<AppSearchParamsInt>): AppSearchParamsExt {
    return {
      t: params.tab?.id,
      q: params.query,
      page: params.page.toString(),
      sort: params.sort?.toString(),
      source: params.source,
      library: params.library,
      tag: params.tags,
      type: params.publicationType,
      productVersionPath: params.productVersionPath?.map(path => path.toString()),
      eosl: params.eosl ? 'true' : null,
      priority: params.priority,
      status: params.status,
      project: params.project,
      entityType: params.entityType
    };
  }

  toEverythingSearchParams(params: Partial<AppSearchParamsInt>, pageSize?: number): EverythingSearchParamsOptions {
    const eoslFilter = (params.eosl) ? null : or(
      value(filter(SearchFilterName.eosl, null, SearchFilterOperator.null)),
      value(filter(SearchFilterName.eosl, 'now', SearchFilterOperator.gt))
    );

    const productVersionPathFilter = this.toProductVersionPathsFilter(params.productVersionPath);

    const nestedFilters = and(
      this.or(SearchFilterName.source, params.source),
      this.or(SearchFilterName.entityType, params.entityType),
      this.or(SearchFilterName.mediaType, params.tab?.mediaTypes),
      this.or(SearchFilterName.group, params.tab?.group),
      this.or(SearchFilterName.publicationType, params.publicationType),
      this.or(SearchFilterName.library, params.library),
      this.and(SearchFilterName.tags, params.tags),
      this.and(SearchFilterName.priority, params.priority),
      this.and(SearchFilterName.status, params.status),
      this.and(SearchFilterName.project, params.project),
      not(value(SearchFilterName.entityType, 'FEED')),
      eoslFilter,
      productVersionPathFilter
    );

    pageSize = params.tab?.count ?? pageSize;

    const sort = params.sort ?? (
      params.query
        ? new SearchSort(SearchSortFieldName._score, SearchSortDirection.desc)
        : new SearchSort(SearchSortFieldName.creationTimestamp, SearchSortDirection.desc)
    );

    return {
      query: params.query,
      offset: (params.page - 1) * pageSize,
      count: pageSize,
      groupBy: params.tab?.groupBy,
      knn: true,
      sort,
      nestedFilters
    };
  }

  private toProductVersionPathsFilter(paths: ProductTreePath[]): ValueFilter {
    if (!paths) {
      return null;
    }
    const partialPaths = paths.flatMap(path => path.toPartialPaths());
    const dedupedPaths = Array.from(new Set(partialPaths));

    return this.or(SearchFilterName.productVersionPaths, dedupedPaths);
  }

  and(filterName: SearchFilterName, values: string | string[]): AndFilter {
    return and(
      ...toStringArray(values).map(it => value(filter(filterName, it)))
    );
  }

  or(filterName: SearchFilterName, values: string | string[]): ValueFilter {
    return value(filter(filterName, values, SearchFilterOperator.in));
  }
}
